/**
 * The hand-written documentation, as data.
 *
 * KEPT AS DATA RATHER THAN AS MARKUP so the portal ships no Markdown renderer
 * and no HTML from a string — this content is rendered into a Vue template with
 * normal interpolation, which means it is escaped by construction. A docs page
 * that renders raw HTML is a stored-XSS surface on the one site that hands out
 * API credentials.
 *
 * WHAT IS HERE IS WHAT THE REFERENCE CANNOT SAY: the shape of the OAuth round
 * trip, why a webhook signature fails, what to do when a token is refused. Every
 * per-endpoint fact belongs in the generated reference instead, where it cannot
 * drift.
 */

const NODE_VERIFY = `const crypto = require("crypto");

// The RAW body. Not req.body, not JSON.stringify(req.body) — a re-serialised
// object has different whitespace and key order, and the signature will not
// match. In Express: express.raw({ type: "application/json" }).
function verify(rawBody, header, secret, toleranceSeconds = 300) {
  const parts = Object.fromEntries(
    header.split(",").map((p) => {
      const i = p.indexOf("=");
      return [p.slice(0, i).trim(), p.slice(i + 1).trim()];
    })
  );

  const timestamp = Number(parts.t);
  if (!timestamp) return false;
  if (Math.abs(Date.now() / 1000 - timestamp) > toleranceSeconds) return false;

  const expected = crypto
    .createHmac("sha256", secret)
    .update(\`\${parts.t}.\${rawBody}\`, "utf8")
    .digest("hex");

  // A rotation sends more than one v1=, so accept if ANY matches.
  return header
    .split(",")
    .filter((p) => p.trim().startsWith("v1="))
    .some((p) => {
      const supplied = Buffer.from(p.trim().slice(3), "utf8");
      const mine = Buffer.from(expected, "utf8");
      return supplied.length === mine.length && crypto.timingSafeEqual(supplied, mine);
    });
}`;

const PYTHON_VERIFY = `import hmac, hashlib, time

def verify(raw_body: bytes, header: str, secret: str, tolerance: int = 300) -> bool:
    parts = dict(p.strip().split("=", 1) for p in header.split(",") if "=" in p)
    try:
        timestamp = int(parts["t"])
    except (KeyError, ValueError):
        return False
    if abs(time.time() - timestamp) > tolerance:
        return False

    expected = hmac.new(
        secret.encode(),
        f"{parts['t']}.".encode() + raw_body,
        hashlib.sha256,
    ).hexdigest()

    # A rotation sends more than one v1=; accept if any matches.
    supplied = [p.strip()[3:] for p in header.split(",") if p.strip().startswith("v1=")]
    return any(hmac.compare_digest(s, expected) for s in supplied)`;

const GO_VERIFY = `func Verify(rawBody []byte, header, secret string, tolerance time.Duration) bool {
	var ts string
	var sigs []string
	for _, part := range strings.Split(header, ",") {
		kv := strings.SplitN(strings.TrimSpace(part), "=", 2)
		if len(kv) != 2 {
			continue
		}
		if kv[0] == "t" {
			ts = kv[1]
		} else if kv[0] == "v1" {
			sigs = append(sigs, kv[1])
		}
	}

	seconds, err := strconv.ParseInt(ts, 10, 64)
	if err != nil {
		return false
	}
	if d := time.Since(time.Unix(seconds, 0)); d > tolerance || d < -tolerance {
		return false
	}

	mac := hmac.New(sha256.New, []byte(secret))
	mac.Write([]byte(ts + "."))
	mac.Write(rawBody)
	expected := hex.EncodeToString(mac.Sum(nil))

	// A rotation sends more than one v1=; accept if any matches.
	for _, s := range sigs {
		if hmac.Equal([]byte(s), []byte(expected)) {
			return true
		}
	}
	return false
}`;

const PHP_VERIFY = `<?php
function zarokaa_verify(string $rawBody, string $header, string $secret, int $tolerance = 300): bool {
    $parts = [];
    foreach (explode(',', $header) as $piece) {
        $kv = explode('=', trim($piece), 2);
        if (count($kv) === 2) { $parts[$kv[0]][] = $kv[1]; }
    }
    if (empty($parts['t'][0])) { return false; }
    $timestamp = (int) $parts['t'][0];
    if (abs(time() - $timestamp) > $tolerance) { return false; }

    $expected = hash_hmac('sha256', $parts['t'][0] . '.' . $rawBody, $secret);

    // A rotation sends more than one v1=; accept if any matches.
    foreach ($parts['v1'] ?? [] as $supplied) {
        if (hash_equals($expected, $supplied)) { return true; }
    }
    return false;
}`;

// Empty base = the hub is proxied under this site's /api, so guides show the portal's own origin.
const HUB = (import.meta.env.VITE_HUB_API_BASE || globalThis.location?.origin || "https://<hub>").replace(/\/+$/, "");

const NODE_INSTALL_TOKEN = `import { createRemoteJWKSet, decodeJwt, jwtVerify } from "jose";

const HUB = "${HUB}";
const trim = (url) => url.replace(/\\/+$/, "");
let directory = { at: 0, byIssuer: new Map() };
const keySets = new Map();

// api_base and every api_bases entry are trusted issuers; white-label domains add extra bases.
async function trustedIssuers() {
  if (Date.now() - directory.at > 5 * 60_000) {
    const res = await fetch(\`\${HUB}/api/registry/v1/directory\`);
    if (!res.ok) throw new Error(\`directory answered \${res.status}\`);
    const { installations } = await res.json();
    const byIssuer = new Map();
    for (const i of installations) {
      for (const base of [i.api_base, ...(i.api_bases || [])].filter(Boolean)) byIssuer.set(trim(base), i);
    }
    directory = { at: Date.now(), byIssuer };
  }
  return directory.byIssuer;
}

// seenJti: "set if absent, expire after 300 s" — Redis SET NX EX in production.
export async function verifyInstallToken(token, clientId, seenJti) {
  const iss = trim(String(decodeJwt(token).iss || "")); // unverified, only picks the issuer
  const installation = (await trustedIssuers()).get(iss);
  if (!installation) throw new Error("issuer is not a listed Zarokaa installation");

  if (!keySets.has(iss)) keySets.set(iss, createRemoteJWKSet(new URL(\`\${iss}/ext/v1/.well-known/jwks.json\`)));
  const { payload } = await jwtVerify(token, keySets.get(iss), {
    issuer: iss,
    audience: clientId,
    algorithms: ["RS256"],
    clockTolerance: 5
  });
  if (payload.token_use !== "install") throw new Error("not an install token");
  if (!(await seenJti.setIfAbsent(payload.jti, 300))) throw new Error("install token replayed");

  return {
    installation: installation.code,
    installId: payload.install_id,
    accountId: payload.account_id,
    scopeMode: payload.scope_mode,
    apiBase: \`\${iss}/ext/v1\`
  };
}`;

const NODE_AUTHORIZE = `import crypto from "node:crypto";

// inst = the result of verifyInstallToken(). Discovery is per installation; never hardcode the URL.
export async function startAuthorize(res, inst, { clientId, redirectUri, scopes, store }) {
  const meta = await (await fetch(\`\${inst.apiBase}/.well-known/oauth-authorization-server\`)).json();
  const verifier = crypto.randomBytes(32).toString("base64url");
  const state = crypto.randomBytes(24).toString("base64url");
  await store.putPending(state, { verifier, ...inst }); // single use, short expiry

  const query = new URLSearchParams({
    response_type: "code",
    client_id: clientId,
    redirect_uri: redirectUri,
    scope: scopes.join(" "),
    state,
    code_challenge: crypto.createHash("sha256").update(verifier).digest("base64url"),
    code_challenge_method: "S256",
    install_id: inst.installId,
    access_type: "offline"
  });
  // e.g. https://fleet.example.com/app/oauth/authorize?...
  res.redirect(\`\${meta.authorization_endpoint}?\${query}\`);
}

export async function callback(req, res, { clientId, clientSecret, redirectUri, store }) {
  const pending = await store.takePending(String(req.query.state || ""));
  if (!pending) return res.status(400).send("Unknown or reused state.");
  if (req.query.error) return res.status(400).send(String(req.query.error));

  const r = await fetch(\`\${pending.apiBase}/oauth/token\`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      code: String(req.query.code),
      redirect_uri: redirectUri,
      client_id: clientId,
      client_secret: clientSecret,
      code_verifier: pending.verifier
    })
  });
  if (!r.ok) return res.status(502).send(\`Token exchange failed (\${r.status}).\`);
  // { access_token, refresh_token, expires_in: 3600, scope, token_type }
  await store.saveTokens([pending.installation, pending.installId], await r.json());
  res.redirect("/");
}`;

const BRIDGE_CONTEXT = `import { createApp } from "@polygon/app-bridge";

// The host origin comes from ?host=; messages from any other origin are ignored.
const bridge = createApp({ clientId: "<your client id>" });

function applyTheme({ theme, tokens }) {
  document.documentElement.dataset.theme = theme;
  for (const [name, value] of Object.entries(tokens || {})) {
    document.documentElement.style.setProperty(name, value);
  }
}

function applyLocale({ locale, dir }) {
  document.documentElement.lang = locale;
  document.documentElement.dir = dir;
}

const context = await bridge.getContext(true); // brand and tokens arrive here, never in the URL
applyTheme(context);
applyLocale(context);

bridge.on("theme", applyTheme);   // also on an OS light/dark flip while theme_mode is "auto"
bridge.on("locale", applyLocale); // also when only dir changes
bridge.on("suspended", ({ reason }) => showSuspended(reason));

const sessionToken = await bridge.getSessionToken(); // 60 s; exchange it on your server

const opened = await bridge.modal.open({ path: "/details" });
if (opened.supported === false) location.assign("/details"); // modal and resize: Phase 10`;

const EXPRESS_FRAME_ANCESTORS = `// webOrigins(): a cached Set of every directory base_url origin plus the origin of every api_bases entry.
app.get("/embed", async (req, res) => {
  const allowed = await webOrigins();
  let host = "";
  try {
    host = new URL(Buffer.from(String(req.query.host || ""), "base64url").toString("utf8")).origin;
  } catch {}

  // With a valid host: exactly that origin. Without one (the review fetch): every origin you serve.
  const ancestors = allowed.has(host) ? host : [...allowed].join(" ");
  res.set("Content-Security-Policy", \`frame-ancestors \${ancestors || "'self'"}\`);
  res.set("Strict-Transport-Security", "max-age=31536000");
  res.send(renderEmbed());
});`;

const TOKEN_EXCHANGE = `curl -X POST '<iss of the session token>/api/ext/v1/oauth/token' \\
  -H 'Content-Type: application/x-www-form-urlencoded' \\
  -d 'grant_type=urn:ietf:params:oauth:grant-type:token-exchange' \\
  -d 'subject_token=<session JWT from bridge.getSessionToken()>' \\
  -d 'subject_token_type=urn:ietf:params:oauth:token-type:jwt' \\
  -d 'client_id=<client id>' \\
  -d 'client_secret=<client secret>'`;

const EXAMPLE_APP_URL = "https://github.com/zarokaa/zarokaa-example-app";

const EXAMPLE_APP_QUICKSTART = `git clone ${EXAMPLE_APP_URL}.git
cd zarokaa-example-app
npm install
npm test                 # self-test: no network, no credentials
cp .env.example .env     # client id and secret from Apps → your app → Credentials
npm start                # http://localhost:3000 prints the URLs to register`;

const NODE_COMMANDS = `import crypto from "node:crypto";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const backoff = (attempt, cap) => Math.min(1000 * 2 ** attempt, cap) * (0.8 + Math.random() * 0.4);

// inst = { apiBase: "<iss>/ext/v1", token } for the installation the device belongs to.
async function zarokaa(inst, path, init = {}) {
  const res = await fetch(\`\${inst.apiBase}\${path}\`, {
    ...init,
    headers: { Authorization: \`Bearer \${inst.token}\`, "Content-Type": "application/json", ...init.headers }
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(body.detail || body.code), { status: res.status, code: body.code });
  return body.data;
}

// One key per intention, made BEFORE the first attempt and reused on every retry of it:
// a retry then returns the same command_id instead of sending the command twice.
export async function sendCommand(inst, deviceId, command, params = {}) {
  const key = \`cmd-\${crypto.randomUUID()}\`;
  for (let attempt = 0; ; attempt++) {
    try {
      return await zarokaa(inst, \`/devices/\${deviceId}/commands\`, {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify({ command, params })
      }); // { command_id, status: "queued", created_at }
    } catch (e) {
      const retryable = !e.status || e.status === 429 || e.status >= 500; // no status = network error
      if (!retryable || attempt === 4) throw e;
      await sleep(backoff(attempt, 30_000));
    }
  }
}

// Webhook path: called after verify() and dedup on event.id (see Webhooks).
export function onCommandEvent(event) {
  if (event.topic !== "command.completed" && event.topic !== "command.failed") return;
  const c = event.data;
  if (c.status === "accepted") {
    console.log(c.command_id, "accepted", c.response?.raw ?? ""); // the device's reply text
  } else if (c.status === "unconfirmed") {
    console.log(c.command_id, "sent; this device never confirms, so it may or may not have run");
  } else {
    console.log(c.command_id, c.status, c.status_reason?.code, c.status_reason?.text);
  }
}

// Fallback when you have no webhook: poll by id, back off, stop at a final status.
export async function waitForCommand(inst, deviceId, commandId, budgetMs = 15 * 60_000) {
  const until = Date.now() + budgetMs;
  for (let attempt = 1; Date.now() < until; attempt++) {
    await sleep(backoff(attempt, 60_000));
    try {
      const c = await zarokaa(inst, \`/devices/\${deviceId}/commands/\${commandId}\`);
      if (c.terminal) return c;
    } catch (e) {
      if (e.status === 404) throw e; // wrong device, or not this account's command: will not change
    }
  }
  return null; // still queued or sent: keep the id and look again later
}`;

const COMMAND_REPLY = `{
  "command_id": "6f1c2a9e-…",
  "command": { "code": "READ_CARDS", "label": "Read registered cards" },
  "status": "accepted",
  "terminal": true,
  "status_reason": null,
  "response": {
    "received_at": "2026-10-21T10:15:02.123Z",
    "raw": "IBUTTONS:0091022C,0,0,0,0,0,0,0",
    "encoding": "text",
    "parser": null,
    "data": null,
    "parse_error": null,
    "truncated": false
  }
}`;

const DEVICE_REPORT = `{
  "id": "evt_dr_5b1f…",
  "topic": "device.report",
  "occurred_at": "2026-10-24T09:12:40.000Z",
  "install_id": "…",
  "account_id": "…",
  "api_version": "v1",
  "data": {
    "device_id": "2f0c…",
    "unit_id": "8a41…",
    "protocol": "queclink",
    "report_code": "GTVDO",
    "status": "301",
    "file_name": "20261024_091030_04_1",
    "device_time": "2026-10-24T09:12:40.000Z",
    "received_at": "2026-10-24T09:12:41.214Z",
    "buffered": false,
    "fields": {
      "source": "20261024_091030_04_1",
      "query_type": "0",
      "protocol_version": "DF0208",
      "device_name": "CV100",
      "count_number": 52741
    }
  }
}`;

// Titles and summaries live in the locale files under guides.<id>; bodies are English only.
const APP_EVENTS_MANIFEST = `"scopes": ["units:read", "units.events:write"],
"events": [
  {
    "code": "pedestrian_ahead",
    "label": "Pedestrian ahead",
    "description": "The forward camera saw a pedestrian in the vehicle's path.",
    "fields": [
      { "key": "confidence", "type": "number", "label": "Confidence (0-1)" },
      { "key": "lane", "type": "string", "label": "Lane" },
      { "key": "night", "type": "boolean", "label": "At night" }
    ]
  }
]`;

const APP_EVENTS_PUBLISH = `POST /units/{unit_id}/events
Authorization: Bearer <access token>
Idempotency-Key: 6f1c0f0e-2d5b-4f7e-9d43-0c1e5b2a7f10
Content-Type: application/json

{
  "code": "pedestrian_ahead",
  "occurred_at": "2026-10-26T09:41:07Z",
  "title": "Pedestrian ahead, 0.93",
  "severity": "warning",
  "location": { "lat": 18.5145, "lng": 73.9263 },
  "fields": { "confidence": 0.93, "lane": "left", "night": false }
}

202 Accepted
{ "event_id": "…", "status": "accepted", "event": "acme-vision.pedestrian_ahead" }`;

export const GUIDES = [
  {
    id: "getting-started",
    blocks: [
      {
        text:
          "A Zarokaa app is hosted entirely by you. Zarokaa never runs your code: it " +
          "issues you credentials, asks the customer to consent, and then answers your " +
          "API calls under the permissions of the person using your app."
      },
      {
        heading: "Start from the example app",
        text:
          "The quickest way in is the open-source example app: plain Node and Express, no " +
          "build step, about 700 lines. It implements every flow on this page — the install " +
          "token, OAuth with PKCE and refresh rotation, REST calls, the embedded UI over the " +
          "app bridge and verified webhooks — and every file says why it does what it does. " +
          "`npm test` runs its self-test with no network and no credentials, so you can read " +
          "and run it before you have registered anything.",
        link: { href: EXAMPLE_APP_URL, label: "github.com/zarokaa/zarokaa-example-app" },
        code: EXAMPLE_APP_QUICKSTART
      },
      {
        heading: "The shape of it",
        steps: [
          "Register an app in this portal once. The same registration reaches every Zarokaa installation you distribute to — ours and white-label partners'.",
          "Generate development credentials. The client secret is shown once, and the credential works on every installation within about a minute.",
          "Invite tester accounts on the installations you want to test against. An unpublished app runs on real data for those accounts only, for up to 90 days.",
          "Implement the install URL (verify the install token) and the OAuth redirect, per installation. Both are covered below.",
          "Fill in the English store listing, turn on two-factor authentication and submit. Automatic checks run at once.",
          "A version that passes every blocking check goes live immediately — publicly or by link only, on all installations or the ones you choose."
        ]
      },
      {
        heading: "Three things that will save you a day",
        text:
          "Every response is enveloped as { data, meta }. Every error is RFC 9457 " +
          "application/problem+json with a stable `code` and a `request_id` worth quoting " +
          "in a support request. And every mutating call needs an Idempotency-Key header " +
          "— it is not optional, and a missing one is a 400 rather than a silent success."
      }
    ]
  },
  {
    id: "installations",
    blocks: [
      {
        text:
          "Zarokaa runs as many separate deployments — ours and white-label partners'. Each " +
          "installation is its own OAuth issuer with its own signing keys, install ids, tokens, " +
          "webhook subscriptions and data. You register once, here; the hub replicates your app, " +
          "listing and credential hashes to every installation you distribute to, within about a " +
          "minute. Everything after the install happens between your server and that one installation."
      },
      {
        heading: "What is per installation",
        steps: [
          "API base: the install token's iss, e.g. https://fleet.example.com/api — call <iss>/ext/v1, never one global URL.",
          "OAuth: authorization_endpoint and token_endpoint come from <iss>/ext/v1/.well-known/oauth-authorization-server.",
          "Keys: <iss>/ext/v1/.well-known/jwks.json signs that installation's install and session tokens.",
          "Tokens, install ids and webhook secrets: store them keyed by (installation, install_id).",
          "Rate limits, suspensions and the partner's own App Store switch."
        ]
      },
      {
        heading: "The flow, end to end",
        steps: [
          "The customer consents in their App Store. The installation records a pending install and sends the browser to your install_url?install_token=<JWT>.",
          "You verify the token (below), store the installation code, install_id and <iss>/ext/v1, and provision nothing else yet.",
          "You redirect the browser to that installation's authorization_endpoint — a page in the Zarokaa web app, ${web}/app/oauth/authorize — with response_type=code, client_id, an exact registered redirect_uri, scope, state, code_challenge (S256), install_id (required) and access_type=offline if you want a refresh token.",
          "The page uses the customer's own Zarokaa session (a signed-out customer signs in and comes back), checks that the install belongs to the account they are signed in to, and sends the browser to your redirect_uri with code and state. A bad PKCE challenge, an unknown client, a redirect_uri that is not an exact registered match or an unknown install_id is shown on that page and never reaches you; a scope wider than the grant comes back as error=invalid_scope.",
          "Within 60 seconds you exchange the code at <iss>/ext/v1/oauth/token with your client_secret and code_verifier. The install becomes active on that first exchange. Access tokens live one hour; refresh tokens 90 days.",
          "Refresh at the same installation. Every refresh returns a new refresh token and invalidates the old one (see Authorisation)."
        ]
      },
      {
        heading: "Install token claims",
        steps: [
          "iss — the installation's API base, https://<domain>/api. A white-label domain of an installation is its own iss.",
          "aud — your client_id for the install's environment: the development credential while the app is unpublished, the live one after.",
          "installation — the hub code of the installation. Omitted when the installation has none configured; take the code from the directory entry instead.",
          "install_id, account_id, scope_mode (\"account\" or \"subtree\"), handle — the pending install.",
          "token_use — always \"install\". Session tokens are signed with the same key and carry \"session\"; never accept one as the other.",
          "jti, iat, exp — a random id; exp is iat + 300 seconds."
        ]
      },
      {
        heading: "The hub directory",
        text:
          `GET ${HUB}/api/registry/v1/directory is public and cacheable for five minutes. It ` +
          "answers { installations: [{ code, name, base_url, api_base, api_bases, jwks_url }] } for " +
          "every active installation listed for developers. api_bases holds api_base first, then " +
          "the installation's extra white-label API bases; accept an iss that matches any of them. " +
          "An issuer that is not in it is not a Zarokaa installation you should trust."
      },
      {
        heading: "Verify the install token",
        steps: [
          "Read iss without trusting it and look it up in the directory (api_base or any api_bases entry). Unknown issuer: reject.",
          "Verify the RS256 signature against <iss>/ext/v1/.well-known/jwks.json. Assert the algorithm; never take it from the header.",
          "Check aud is your client id, exp has not passed (five-minute tokens, five seconds of skew) and token_use is \"install\".",
          "Reject a jti you have already seen. Zarokaa does not track install-token use; single use is your job.",
          "Store the installation code, install_id and <iss>/ext/v1, then start OAuth against that installation with install_id on the authorize request."
        ],
        code: NODE_INSTALL_TOKEN
      },
      {
        heading: "Authorize and exchange, per installation",
        text:
          "Keep the installation with the PKCE state, and exchange the code at the installation " +
          "that issued it. Tokens are stored under (installation, install_id).",
        code: NODE_AUTHORIZE
      },
      {
        heading: "After a suspension is lifted",
        text:
          "A suspension or block revokes every access and refresh token of the app's installs. " +
          "Lifting it restores the app and its webhook subscriptions, but not offline access: no " +
          "refresh token is reissued. Your next refresh answers EXT_INVALID_TOKEN while webhooks " +
          "keep arriving.\n\n" +
          "Treat EXT_INVALID_TOKEN on a refresh as \"reconnect needed\" for that (installation, " +
          "install_id) and send a user through the authorize page again with the same install_id — " +
          "it is accepted for an active install. Your embedded UI still works in the meantime, " +
          "because the session-token exchange issues online tokens again as soon as the hold is " +
          "lifted, so a Reconnect button there is the simplest path."
      },
      { see: ["review"] },
      {
        heading: "Testing against several installations",
        text:
          "Invite tester accounts per installation from the app's Testers tab, and restrict an " +
          "early release with Distribution → only the installations I choose. The example app " +
          "(github.com/zarokaa/zarokaa-example-app) implements all of the above and has a self-test that rejects an " +
          "unknown issuer and a reused jti."
      }
    ]
  },
  {
    id: "oauth",
    blocks: [
      {
        text:
          "Authorization code with PKCE (S256). There is no client-credentials grant and " +
          "no implicit grant: a token that is not tied to an installation and a human " +
          "identity has no account to scope to and no roles to intersect with, so it " +
          "cannot exist here."
      },
      {
        heading: "The round trip",
        steps: [
          "The customer installs your app. Their installation redirects the browser to your install_url with ?install_token=<JWT> — verify it (see \"One app, many Zarokaa installations\") before you provision anything.",
          "You redirect them to that installation's authorization_endpoint (from its OAuth discovery document) with your client_id, an exact registered redirect_uri, the scopes you want, a code_challenge and the install_id.",
          "Zarokaa redirects back to your redirect_uri with a code. It is single-use and lives 60 seconds.",
          "You exchange the code at /oauth/token with your client_secret and code_verifier.",
          "You get an access token and, if you asked for access_type=offline, a refresh token."
        ]
      },
      {
        heading: "Refresh tokens rotate",
        text:
          "Every refresh returns a NEW refresh token and invalidates the one you used. " +
          "Store the new one before you use the access token. Presenting a rotated " +
          "refresh token means it was captured, so we revoke every token for that " +
          "installation and raise a token_replay event — which is a suspension, not a " +
          "warning. If you run several workers, make sure only one of them refreshes."
      },
      {
        heading: "Online and offline tokens",
        text:
          "A token minted for someone using your embedded UI acts as that person and " +
          "expires with their session. An offline token, for your background jobs, acts " +
          "as the user who installed the app. Both are still checked against that user's " +
          "live RBAC on every call — a token does not outlive the permissions behind it."
      },
      {
        heading: "Exchanging a code",
        code: `curl -X POST '<iss>/ext/v1/oauth/token' \\
  -H 'Content-Type: application/x-www-form-urlencoded' \\
  -d 'grant_type=authorization_code' \\
  -d 'code=<code>' \\
  -d 'redirect_uri=<your exact registered uri>' \\
  -d 'client_id=<client id>' \\
  -d 'client_secret=<client secret>' \\
  -d 'code_verifier=<the verifier for your challenge>'`
      }
    ]
  },
  {
    id: "errors",
    blocks: [
      {
        text:
          "Every failure is problem+json with the same shape. Read `code`, not the HTTP " +
          "status alone: several distinct conditions share a 403, and only the code tells " +
          "them apart."
      },
      {
        heading: "The ones worth handling separately",
        text:
          "EXT_INSUFFICIENT_SCOPE — the customer never granted this. Do not retry; ask " +
          "them to re-consent.\n" +
          "EXT_ACCESS_DENIED — the acting user's role does not allow it. Another user " +
          "might succeed.\n" +
          "EXT_INSTALL_INACTIVE — uninstalled or suspended. Stop calling.\n" +
          "EXT_RATE_LIMITED — back off for Retry-After seconds.\n" +
          "EXT_QUOTA_EXCEEDED — the daily allowance is gone; retrying before midnight UTC " +
          "will not help.\n" +
          "EXT_UPSTREAM_TIMEOUT — ours, transient, retry with backoff."
      },
      {
        heading: "Rate limits",
        text:
          "Limits are per INSTALLATION, not per app, so one customer's runaway loop " +
          "cannot starve your other customers. Every response carries RateLimit-Limit, " +
          "RateLimit-Remaining and RateLimit-Reset; a 429 also carries Retry-After. " +
          "Expensive endpoints — reports, history, video sessions — have a second, " +
          "tighter budget of their own."
      }
    ]
  },
  {
    id: "webhooks",
    blocks: [
      {
        text:
          "Polling a fleet API for changes is how a platform gets crushed, so subscribe " +
          "instead. You register an endpoint per install on each installation (POST /webhooks " +
          "with webhooks:manage), choose topics, and we POST to it. A topic is only deliverable " +
          "if that install granted the scope that would let you read the same data through the " +
          "API; GET /webhooks/topics lists what the current grant allows, and narrowing consent " +
          "takes effect on the next event."
      },
      {
        heading: "Topics and the scope each needs",
        steps: [
          "unit.created, unit.updated, unit.deleted — units:read.",
          "unit.position — units.positions:read, plus an explicit unit filter (below).",
          "event.created — alerts:read. Any Alert V2 incident opening on a vehicle, geofence rules included. event_id is the incident id for GET /alerts/incidents/{incident_id}; type is the rule's template key. It needed units.events:read before Alert V2.",
          "geofence.entered, geofence.exited — geofences:read. Produced by the customer's geofence entry/exit alert rules, so no rule means no event. One geofence incident sends both event.created and geofence.entered when both scopes are granted.",
          "command.status_changed, command.completed, command.failed — devices.commands:execute. Exactly one of completed or failed per command, once it is final; status_changed fires on every change and is optional. See \"Send a command and track its result\".",
          "device.report — devices.reports:read. A report or response from a device that is not a position; see \"device.report\" below.",
          "video.session.started, video.session.ended — video:write; video.recording.ready — video:read.",
          "report.run.completed — reports:read.",
          "install.created, install.scopes_updated, install.uninstalled, entitlement.updated — lifecycle topics; they need only webhooks:manage."
        ]
      },
      { see: ["commands"] },
      {
        heading: "What arrives",
        text:
          "The body is { id, topic, occurred_at, install_id, account_id, api_version: \"v1\", data }, " +
          "with data narrowed to the topic's declared fields. Headers: X-Polygon-Signature, " +
          "X-Polygon-Topic, X-Polygon-Delivery-Id (the event id), X-Polygon-Attempt and " +
          "X-Polygon-Api-Version.\n\n" +
          "The body names the install, not the installation, and each subscription has its own " +
          "secret. Give every subscription its own URL path — e.g. /webhooks/zarokaa/<installation>/<install_id> " +
          "— so you can pick the right secret before you verify anything."
      },
      {
        heading: "Delivery is at-least-once and unordered",
        text:
          "Deduplicate on the event `id`, which is stable across every retry and every " +
          "replay. Do not infer ordering from arrival: two events about the same vehicle " +
          "can arrive out of order, and `occurred_at` is the only thing that says when " +
          "each happened.\n\n" +
          "Answer 2xx as soon as you have durably accepted the event, and do your work " +
          "afterwards. We time out at 10 seconds; anything slower will be retried while " +
          "your handler is still running."
      },
      {
        heading: "How fast",
        text:
          "Fan-out happens when the event is recorded; the dispatcher runs once a minute, and " +
          "alert-derived topics (event.created, geofence.*) wait 15 seconds for the incident to " +
          "settle. End to end we measured 15 to 58 seconds from the change to your endpoint. " +
          "Do not build anything that needs sub-second delivery on webhooks."
      },
      {
        heading: "Retries, auto-disable and replay",
        text:
          "Eight attempts over roughly a day: at once, then ~10s, ~1m, ~5m, ~32m, ~2h, ~6h and " +
          "~18h, each ±20%. Any non-2xx or timeout is retried EXCEPT a 4xx that is not 408 or 429 " +
          "— those mean you understood us and said no, and sending it seven more times changes " +
          "nothing.\n\n" +
          "An endpoint is disabled after 100 consecutive failures, or once it has failed for 24 " +
          "hours since its last success, whichever comes first. That disables the endpoint, never " +
          "your app. Through that installation's API: GET /webhooks/:id/deliveries to see what " +
          "failed, PATCH /webhooks/:id with status \"active\" to re-enable, POST " +
          "/webhooks/deliveries/:delivery_id/replay to resend (same event id), and POST " +
          "/webhooks/:id/rotate-secret to rotate."
      },
      {
        heading: "Verify every request",
        text:
          "X-Polygon-Signature: t=<unix seconds>,v1=<hex hmac-sha256>. The signed string " +
          "is the timestamp, a literal dot, then the RAW request body. Reject anything " +
          "older than five minutes.\n\n" +
          "Sign the raw bytes. Verifying against a re-serialised body is the single most " +
          "common cause of signature failures — key order and whitespace differ from what " +
          "we sent. During a secret rotation we send TWO v1= values, so accept if either " +
          "matches.",
        langs: ["Node", "Python", "Go", "PHP"],
        code: {
          Node: NODE_VERIFY,
          Python: PYTHON_VERIFY,
          Go: GO_VERIFY,
          PHP: PHP_VERIFY
        }
      },
      {
        heading: "device.report",
        text:
          "A device report is anything a device sends that is not a position: the result of an operation it " +
          "ran — a stream request accepted or refused, a file upload moving through its steps, a page of a " +
          "file listing. Each one is a device.report event. It is not an alert and it is not stored, so " +
          "subscribe if you need it.\n\n" +
          "data: device_id; unit_id (the vehicle the device is fitted to now, or null); protocol (the " +
          "device protocol, e.g. queclink); report_code (the device's own report name); status (its result " +
          "code, verbatim, or null when the report has none); file_name (when the report names one file); " +
          "device_time (the time the device wrote on the report, or null; occurred_at is device_time, else " +
          "received_at); received_at; buffered (true when the device stored the report offline and sent it " +
          "later); fields (everything else the platform parsed, snake_case keys).\n\n" +
          "The set of report codes depends on the device protocol, and more protocols will add theirs " +
          "without a change to this contract; read codes against the device's protocol document. " +
          "Current table, Queclink: GTSOD (stream request: 1X0 ok, 1X2 URL unreachable, 1X7 busy, 1X8 " +
          "refused with ignition off or storage removed; X, the stream type, is also fields.stream_type), " +
          "GTVDO (file upload: 100 confirmed, 200 prepared, 300 uploading, 301 done, 302 failed; " +
          "fields.query_type), GTPIC (picture upload, with the position it was taken at), GTRFQ (file " +
          "listing page: fields.records [{ file_name, size_kb }], record_count, total_frames, frame_number), " +
          "GTFTP and GTAWS (automatic upload to FTP or S3: 0 started, 1 finished, 2 failed; position and " +
          "cell in fields). fields.latitude and fields.longitude appear only when the device had a fix, which " +
          "is why the topic has its own sensitive scope.\n\n" +
          "The event id is derived from the report, so a redelivery or a report resent from the device's " +
          "buffer carries the same id: deduplicate on it as for every topic.",
        code: DEVICE_REPORT
      },
      {
        heading: "unit.position needs a filter",
        text:
          "It is the one topic that can drown both of us, so it is opt-in per vehicle: " +
          "you must name the units, at most 500 per subscription, and each one is " +
          "throttled to your position_interval_s (minimum 30 seconds). A subscription " +
          "without a unit list is refused rather than silently delivering the whole fleet."
      },
      {
        heading: "install.uninstalled and the one-hour grace",
        text:
          "The one webhook every app needs. An uninstall publishes install.uninstalled first, then " +
          "disables every subscription of that install. Fleet topics stop at once; lifecycle " +
          "deliveries keep going to those endpoints, retries included, for one hour after the " +
          "uninstall, and are dropped after that. An endpoint disabled by an uninstall cannot be " +
          "re-enabled. The developer agreement requires you to delete the customer's data within " +
          "30 days."
      },
      {
        heading: "Suspensions",
        text:
          "When the app or the install is suspended, its subscriptions are disabled and events in " +
          "that period are not queued for you. Reinstatement re-enables exactly the endpoints the " +
          "suspension disabled, with their failure count reset."
      }
    ]
  },
  {
    id: "commands",
    blocks: [
      {
        text:
          "A tracking device is not a web server. It is on a mobile network, it may be asleep or out of " +
          "coverage, and some protocols never say whether a command was carried out. So sending a command " +
          "never answers with its result: POST /devices/{device_id}/commands answers 201 with " +
          "{ command_id, status: \"queued\", created_at } as soon as the command is stored, and the result " +
          "comes later — by webhook, or by reading the command back."
      },
      {
        heading: "The flow",
        steps: [
          "Send it: POST /devices/{device_id}/commands with { command, params, unit_id? } and an Idempotency-Key. command is the code from the device model's command list, e.g. READ_CARDS; unit_id is optional and, when given, the device must be fitted to that vehicle. Scope devices.commands:execute, and the acting user needs permission to send commands, not only to view devices.",
          "Keep the command_id. Webhooks and every read identify the command by it.",
          "Preferred: subscribe to command.completed and command.failed. Exactly one of the two arrives for every command, once it is final, with the full record — the reply included.",
          "Without a webhook: GET /devices/{device_id}/commands/{command_id} with exponential backoff (start at a few seconds, cap at a minute) and stop as soon as terminal is true. Polling every second does not make a device answer sooner; it only spends your rate limit.",
          "For history: GET /devices/{device_id}/commands?status=… lists the device's commands, newest first. Follow links.next (it carries a cursor, so deep history costs the same as the first page) until it is null. Reads take devices.commands:read; devices:read and devices.commands:execute are accepted too."
        ]
      },
      {
        heading: "Statuses",
        steps: [
          "queued — stored and waiting to go out. A device that is offline keeps it here until it connects or the command expires.",
          "sent — written to the device's connection; waiting for its reply.",
          "accepted (final) — the device confirmed it. response holds the reply.",
          "rejected (final) — the device answered and refused it: wrong parameters, unsupported, a password it did not accept. status_reason.text is the device's own wording.",
          "unconfirmed (final) — sent to a device whose protocol gives no confirmation. See below.",
          "timeout (final) — sent, but no reply arrived in time: 90 seconds after sending unless the command sets its own (Galileosky READ_CARDS waits 120).",
          "expired (final) — never sent, because the device did not connect before the command's expiry.",
          "failed (final) — could not be written or queued at all."
        ]
      },
      {
        text:
          "terminal is true for the last six, and a final status never changes; a reply that arrives after " +
          "a timeout does not turn it into accepted. status_reason is { code, text } on rejected, timeout, " +
          "expired and failed — code is one of device_rejected, no_device_ack, ack_timeout, expired, " +
          "write_failed, retries_exhausted, queue_failed, unsupported_by_protocol or invalid_raw_command — " +
          "and null otherwise.\n\n" +
          "Commands reach a device one at a time, in the order they were sent: the next one waits until the " +
          "previous is settled or has timed out. Ten commands queued for an offline device go out in order " +
          "when it connects, not all at once."
      },
      {
        heading: "unconfirmed is not accepted",
        text:
          "Some protocols have no acknowledgement for commands at all: the platform writes the bytes and " +
          "the device says nothing either way. Those commands end unconfirmed, and command.completed " +
          "carries them, because nothing went wrong that the platform can see. It is not evidence that the " +
          "command ran.\n\n" +
          "Never treat unconfirmed as accepted for anything that matters — an immobiliser, a door, an " +
          "output. Show it as \"sent, not confirmed\", and check the effect another way: the next position " +
          "packet, an input or sensor change, or ask the user."
      },
      {
        heading: "Retries and the Idempotency-Key",
        text:
          "The key is required: a POST without one is a 400 (EXT_MISSING_IDEMPOTENCY_KEY), because a " +
          "network retry that sends an immobilise command twice is not a harmless duplicate. Make one key " +
          "per intention before the first attempt — 8 to 255 characters of A-Z a-z 0-9 . _ : - — and reuse " +
          "it on every retry of that intention. Within 24 hours a retry returns the original command_id " +
          "with its current status and sends nothing; the same key with a different body is a 409 " +
          "(EXT_IDEMPOTENCY_CONFLICT).\n\n" +
          "Retry on a network error, 429 (after Retry-After) or 5xx. Do not retry a 4xx: EXT_NOT_FOUND " +
          "means the device is unknown or not in this account (the two look the same on purpose), " +
          "EXT_INVALID_PARAMETER an unknown command code or missing parameter, EXT_UNPROCESSABLE a " +
          "command this device model does not support or a device that is not on unit_id, " +
          "EXT_COMMAND_UNSUPPORTED_BY_PROTOCOL a RAW_BINARY command to a device whose protocol cannot " +
          "frame one. Sending has its own, tighter rate limit."
      },
      {
        heading: "Binary protocols: RAW_BINARY",
        text:
          "Some protocols, JT/T 808 first, take binary messages that only the platform can address: the " +
          "header carries a per-connection serial number and the terminal's id, and the frame needs a " +
          "checksum and byte escaping. Build the message body yourself and send it as the generic command " +
          "RAW_BINARY: params { message_id, body_hex }. message_id is the protocol's message type, an " +
          "integer 0–65535 or a \"0x\" string (\"0x9101\" asks a JT/T 1078 terminal to start live video); " +
          "body_hex is the body in hex, at most 1024 bytes, and may be empty. The platform adds everything " +
          "else and the device receives a complete message.\n\n" +
          "It then behaves as any other command: one at a time per device, settled by the device's reply. " +
          "On JT/T 808 the terminal's general response settles it — result 0 is accepted; 1 (failed), " +
          "2 (bad message) and 3 (unsupported) are rejected with the result in status_reason.text — and a " +
          "query answer such as the 0x1205 resource list becomes the command's response (and also arrives " +
          "as a device.report webhook). Invalid hex, an oversized body or a missing message_id is a 400 " +
          "(EXT_INVALID_PARAMETER); any protocol other than JT/T 808 is refused with " +
          "EXT_COMMAND_UNSUPPORTED_BY_PROTOCOL and nothing is sent. Text protocols keep using CUSTOM."
      },
      {
        heading: "Webhooks",
        steps: [
          "command.completed — the command ended accepted or unconfirmed.",
          "command.failed — it ended rejected, timeout, expired or failed.",
          "command.status_changed — optional; every change, queued → sent → final. Subscribe only if you show live progress; the final status also fires one of the two above.",
          "All three need devices.commands:execute. data is the same record as GET /devices/{device_id}/commands/{command_id}: command_id, device_id, unit_id, command { code, label }, status, terminal, status_reason, created_at, sent_at, acknowledged_at, completed_at, activity, response, source and params with secrets redacted.",
          "Delivery is at-least-once and unordered: deduplicate on the event id, and do not let a late status_changed overwrite a final status you already hold — check terminal."
        ]
      },
      { see: ["webhooks"] },
      {
        heading: "Reading data from a device",
        text:
          "Some commands exist to read something: a configuration value, a counter, a list the device " +
          "keeps. The device's reply is kept on the command as response, as text:\n\n" +
          "received_at — when the reply arrived.\n" +
          "raw — the reply exactly as the device sent it: text, or hex when encoding is \"hex\". Always " +
          "kept, cut at 16 KiB with truncated: true.\n" +
          "parser, data, parse_error — null for nearly every device. A few protocols whose replies have a " +
          "fixed frame fill them (e.g. jointech.reply splits a reply into { code, args }).\n\n" +
          "The platform does not interpret what a reply says: read raw and parse it in your app for the " +
          "commands you care about. Whether the device accepted or rejected the command is already in " +
          "status. Example: Galileosky \"Read registered cards\" (code READ_CARDS) answers its monitored " +
          "iButton list as one line of text.",
        code: COMMAND_REPLY
      },
      {
        heading: "Which devices confirm",
        text:
          "Whether a command can end accepted depends on the device's protocol. What the platform does " +
          "with each, today — a particular model or firmware may still behave differently, and timeout or " +
          "unconfirmed is how you find out:",
        steps: [
          "Galileosky — yes. The reply echoes the command's number, so it is matched exactly. The protocol has no result code, so the reply text decides: a known error (e.g. -1, -3) is rejected.",
          "EGTS — yes. Matched by command id, with an explicit result code (OK, or ERROR, ILL, NFOUND, NCONF… as rejected). Default timeout 120 s.",
          "Concox (GT06 family) — yes. Matched by the server flag; Success/OK is accepted, Command Error or Fail rejected.",
          "Queclink @Track — yes, matched by serial number. The protocol has no negative reply: a wrong password or malformed command is silently ignored and ends in timeout. Read commands (AT+GTRTO) settle on the report that follows the ACK.",
          "Navtelecom (NTCB / FLEX) — yes. The reply echoes the command's mnemonic with OK or FAIL; an unsupported command is rejected.",
          "Teltonika (Codec 12) — yes. The text reply is matched in order; error wording is rejected.",
          "Ruptela — yes, one command in flight. A text reply, or a one-byte result for IO commands. Commands the device never answers are set up to end unconfirmed.",
          "Jointech (JT701 family) — yes. The reply echoes the order code with a result field.",
          "Wialon IPS — only when the device answers with a text message. Many devices never answer custom commands; those are set up to end unconfirmed, the rest end in timeout.",
          "Arnavi — when the device answers with a text or confirm packet; the reply text decides, one command in flight.",
          "Wialon Combine — when the device sends an ACK for a custom command (code 0 accepted, 1–3 rejected). The ACK is optional in the protocol, so models that never send it are set up to end unconfirmed.",
          "Polymobile — as Wialon IPS.",
          "Autofon and Mabat — no commands over the data connection."
        ]
      },
      {
        heading: "Send, receive, poll — in Node",
        text:
          "Three pieces: sending with a stable Idempotency-Key and safe retries, handling the final " +
          "webhook, and polling as a fallback. The example app (github.com/zarokaa/zarokaa-example-app) " +
          "has the same flow wired to a page and a self-test.",
        code: NODE_COMMANDS
      }
    ]
  },
  {
    id: "app-events",
    blocks: [
      {
        text:
          "Your app may see things about a vehicle that Zarokaa cannot: a camera that spots a " +
          "pedestrian, a sensor platform that measures load, a driver app that hears a panic word. " +
          "Publish them as unit events and customers can write alert rules on them, with the same " +
          "notifications, escalation and incident history as their own alerts. There is no endpoint " +
          "that creates an alert or incident directly: publishing reports a fact, and the customer's " +
          "rules decide whether it is worth an alert."
      },
      {
        heading: "The flow",
        steps: [
          "Declare each event in the version manifest, under events, with its code and the typed fields it carries. Declaring events needs the units.events:write scope.",
          "Submit the version. The manifest check validates the declarations, and the review message lists the events customers can write rules on.",
          "After a customer installs that version, the alert builder offers the template \"App event\": the customer picks your app's event, and optionally a field with a threshold (at least, at most or equal to a number, or equal to a text for string and boolean fields).",
          "Publish: POST /units/{unit_id}/events with an Idempotency-Key and { code, occurred_at?, title?, severity?, location?, fields? }. It answers 202 with { event_id, status: \"accepted\", event } once the event is queued.",
          "When a rule matches, the incident opens, its notifications go out and event.created is delivered with data.app_event = { app, code, event_id, title, fields }, the fields typed as you sent them."
        ]
      },
      {
        heading: "Declaring events",
        text:
          "code is lower-case letters, digits and _, starting with a letter, at most 48 characters, and " +
          "unique within the version; at most 50 events. Each field has a key (the same rule), a type — " +
          "number, string or boolean — and an optional label; at most 32 fields per event. label and " +
          "description are what customers read in the alert builder, so write them for a fleet manager. " +
          "These keys are reserved and refused as field keys: event, app, code, event_id, title, severity, " +
          "stage, lat, lng and fields.\n\n" +
          "Codes are namespaced by your app's handle: rules match <handle>.<code>, so another app's " +
          "pedestrian_ahead never fires your customers' rules, and yours never fires theirs.",
        code: APP_EVENTS_MANIFEST
      },
      {
        heading: "Publishing",
        text:
          "unit_id is a vehicle the person who installed the app can see; any other is EXT_NOT_FOUND. occurred_at is RFC 3339, within the last 24 hours and at most 5 minutes ahead; it " +
          "defaults to now. title is at most 200 characters and is what the incident and notifications " +
          "show. severity — info, warning or critical — is a hint only: the customer's rule sets the " +
          "incident's severity. Send location when the event happened somewhere other than the vehicle's " +
          "last reported position. fields carries only declared keys, each a flat value of its declared " +
          "type, strings at most 1000 characters.\n\n" +
          "Only the customer's installed version counts. A code that version does not declare is refused " +
          "with EXT_EVENT_UNDECLARED, a wrong field with EXT_EVENT_FIELD_INVALID (both 422, with the " +
          "offending field in the problem). Ship the declaration first, and keep publishing an old code " +
          "until every customer has moved off the versions that declare it.",
        code: APP_EVENTS_PUBLISH
      },
      {
        heading: "Retries, rate and what accepted means",
        text:
          "202 means queued for the customer's rules, not that any rule matched; there may be none. " +
          "Retry a network error, 429 (after Retry-After) or 5xx with the same Idempotency-Key: within " +
          "24 hours the original answer comes back and nothing is published twice. Each event is its own " +
          "incident, keyed by its event_id, so publish once per occurrence rather than once per frame or " +
          "sample. Publishing uses the write rate class."
      },
      {
        heading: "When the app is uninstalled",
        text:
          "Uninstalling switches off every rule on that app's events in the installing account, and in " +
          "its sub-accounts when the installation reached them. The rules are kept, shown as switched off because the app was uninstalled, and " +
          "a customer can switch them back on after reinstalling."
      },
      { see: ["webhooks", "review"] }
    ]
  },
  {
    id: "embedding",
    blocks: [
      {
        text:
          "A full-page app appears in the customer's sidebar and opens at /apps/<handle>, " +
          "rendering your app_url in a sandboxed iframe. The URL carries a signed session JWT " +
          "and a presentation context; @polygon/app-bridge keeps both current."
      },
      {
        heading: "The iframe URL",
        text:
          "<app_url>?session=<JWT>&install_id=…&locale=…&dir=…&theme=…&theme_mode=…" +
          "&theme_color=…&unit_system=…&timezone=…&installation=…&host=<base64url origin>\n\n" +
          "theme_mode, theme_color, timezone and installation are omitted when unknown. brand and " +
          "tokens are never in the URL (URLs end up in logs and referrers); they are in the " +
          "session JWT and in the bridge context."
      },
      {
        heading: "The context (protocol 1, context 1.1)",
        steps: [
          "install_id — the install on this installation.",
          "locale — the user's language, e.g. en, ru, he. dir — \"ltr\" or \"rtl\".",
          "theme — \"light\" or \"dark\": what Zarokaa shows right now. theme_mode — \"light\", \"dark\" or \"auto\" (follows the OS). (1.1)",
          "theme_color — the deployment's accent colour, white-label aware, e.g. #7367F0.",
          "brand — { name, logo_url } of the (white-label) deployment; logo_url is https only and dropped otherwise. (1.1)",
          "unit_system — \"metric\" or \"imperial\". timezone — IANA, e.g. Asia/Kolkata.",
          "installation — the hub installation code; the deployment's own configuration wins over anything the browser claims. (1.1)",
          "tokens — CSS custom properties resolved to concrete values (below). (1.1)"
        ]
      },
      {
        heading: "Events: theme and locale",
        text:
          "theme — { theme, theme_mode, theme_color, brand, tokens }, plus the 1.0 aliases mode " +
          "(= theme) and color (= theme_color). Sent when the user switches light/dark or changes " +
          "mode, and, while theme_mode is \"auto\", when the operating system flips — after " +
          "Zarokaa has re-skinned, so you follow what the customer sees. An unchanged payload is " +
          "not re-sent.\n\n" +
          "locale — { locale, dir }. Sent when the language changes and also when only the " +
          "direction changes, so an RTL switch alone reaches you.\n\n" +
          "suspended — { reason }. The bridge drops its cached session token and getSessionToken() " +
          "rejects with EXT_APP_SUSPENDED; Zarokaa shows its own suspended panel."
      },
      {
        heading: "CSS tokens",
        text:
          "--polygon-primary, --polygon-surface, --polygon-text, --polygon-border, " +
          "--polygon-radius and --polygon-space-1 to --polygon-space-4. Values are concrete — " +
          "colours, lengths, rgb()/rgba() — never url() or expression(), at most 16 of them, 64 " +
          "characters each. Apply them with style.setProperty and keep your own fallbacks for the " +
          "first paint."
      },
      {
        heading: "Talk to the host through the bridge",
        text:
          "@polygon/app-bridge is the only supported channel; raw postMessage is not a contract. " +
          "It answers handshake, getSessionToken (a fresh 60-second token per call), getContext, " +
          "navigate (Zarokaa paths only, e.g. /unit/123/view), toast (text up to 500 characters, tone " +
          "info, success, warning or error) and loading. modal.open, modal.close and resize answer " +
          "{ supported: false, phase: \"10\" } until the embedded panels of Phase 10; anything " +
          "unknown answers { supported: false }.",
        code: BRIDGE_CONTEXT
      },
      {
        heading: "The host origin",
        text:
          "host is the base64url-encoded origin of the Zarokaa web app that frames you — the " +
          "installation's own domain, or its white-label domain. The bridge posts only to that " +
          "origin and accepts messages only from it. Accept a host only when it is a directory " +
          "base_url or the origin of one of an installation's api_bases."
      },
      {
        heading: "You must serve the right frame-ancestors",
        text:
          "Every installation frames you from its own origin, so your Content-Security-Policy " +
          "frame-ancestors must cover every origin you serve: a host wildcard you control " +
          "(https://*.example.com), or — the usual answer — the validated host per request. The " +
          "review and the nightly re-check fetch app_url without host and without a session, so " +
          "that response (and anything it redirects to) needs a restrictive default too: never " +
          "'none', '*' or a bare scheme. An origin missing from the policy is a blank panel for " +
          "that installation's customers, and no check can see it.",
        code: EXPRESS_FRAME_ANCESTORS
      },
      {
        heading: "What the sandbox allows",
        text:
          "sandbox=\"allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox " +
          "allow-downloads allow-same-origin\", allow=\"fullscreen; clipboard-write\", " +
          "referrerpolicy=\"strict-origin\".\n\n" +
          "Manifest capabilities widen allow, and only for the installed version that declares " +
          "them. The one capability today is microphone: a version with capabilities: " +
          "[\"microphone\"] is framed with allow=\"fullscreen; clipboard-write; microphone\", so " +
          "getUserMedia({ audio: true }) works inside the iframe (the browser still asks the user " +
          "the first time). Any other name fails the manifest check. A customer gets the grant " +
          "when they move to that version; the sandbox flags never change.\n\n" +
          "allow-same-origin gives you your OWN origin for storage and XHR; it is safe only " +
          "because your app is served from an origin other than Zarokaa's. There is no " +
          "allow-top-navigation (ask with navigate()), no allow-modals (alert, confirm and prompt " +
          "do nothing) and no camera, screen capture or geolocation; no microphone unless you declare it. OAuth or payment popups on your " +
          "own domain work."
      },
      {
        heading: "The query string is a convenience, not a source of truth",
        text:
          "Everything security-relevant is also inside the signed session JWT (60 seconds, aud = " +
          "your client id, token_use \"session\"). Its iss is the installation's origin WITHOUT " +
          "/api, so its keys are at <iss>/api/ext/v1/.well-known/jwks.json; trust the iss only " +
          "when <iss>/api is in the directory. Usually you do not verify it yourself: your server " +
          "exchanges it at that installation's token endpoint for an online token acting as the " +
          "signed-in user, and the exchange verifies it. A session JWT works once.",
        code: TOKEN_EXCHANGE
      }
    ]
  },
  {
    id: "review",
    blocks: [
      {
        text:
          "There is no manual approval. Submitting a version (two-factor authentication required) " +
          "runs seven automatic checks in the hub, usually within seconds. If no blocking check " +
          "fails, the version goes live at once: it supersedes the previous live version and the " +
          "app becomes published (public listing) or private (link only), distributed to all " +
          "installations or your allow-list. Installations pick the change up on their next sync, " +
          "within a minute. Live credentials can be created only after the first publish.\n\n" +
          "A blocking failure marks the version failed; a version that was already live stays " +
          "live. Warnings never stop a publish. Every result, with its evidence, is on the " +
          "version's Checks panel and in Notices, and the owners get an e-mail."
      },
      {
        heading: "The checks and their exact pass conditions",
        steps: [
          "manifest (blocking) — version is 1–32 letters, digits, \".\", \"-\" or \"_\"; app_url and install_url are HTTPS (http://localhost, 127.0.0.1 or [::1] only while the app has no live credential); 1–5 redirect URIs, each an HTTPS URL with no * and no #fragment; at least one scope, all known (notifications:* is now unknown — use alerts:*); justifications only for requested scopes, each at least 20 characters; extension point types full_page, unit_detail_tab or unit_row_action; every webhook topic known and covered by its scope (lifecycle topics by webhooks:manage); capabilities, if any, from microphone only; events, if any, well formed (see \"Publish events that raise alerts\") and declared only with the units.events:write scope, with the line \"The app publishes N unit event(s) customers can write alert rules on: …\" added to the message. A version that declares microphone passes with the line \"The app requests microphone (audio input) access.\" added to the message, never a failure. Evidence: the environment judged, each problem and the declared capabilities.",
          "listing (blocking) — an en listing with a tagline, a description, a privacy URL, a support URL, a data-retention statement and at least one screenshot. Evidence: the missing items.",
          "https (blocking) — app_url, install_url, privacy_url and support_url fetched with GET over HTTPS with a valid certificate. app_url and install_url may answer any status up to 499 (no session is sent); privacy and support pages must answer below 400. At most 3 redirects, all to https; public addresses only; 10-second timeout; 502/503/504 and transient network errors retried up to 3 times. Evidence per URL: status, final_url, redirects, elapsed_ms, attempts, or the error kind (dns, timeout, tls, not_https, insecure_redirect, blocked_address, too_many_redirects).",
          "frame_ancestors (blocking) — the final response for app_url carries a Content-Security-Policy frame-ancestors with at least one source and none of 'none', * or a bare scheme such as https:. Host wildcards like https://*.example.com pass. Evidence: the CSP headers, the parsed sources, and X-Frame-Options for information.",
          "oauth_probe (warning; blocking when it fails for the third review in a row) — GET install_url?install_token=<probe token>. Any HTTP status below 500 passes; 5xx, a timeout, a network or TLS error, or having no active credential fails. Evidence: status, client_id, environment, redirects and final_url with the token redacted, consecutive_failures and blocks_after (3).",
          "security_headers (warning) — app_url and install_url send Strict-Transport-Security with max-age above 0, and install_url does not send Access-Control-Allow-Origin: *. Evidence: the header values.",
          "developer_standing (blocking) — your organisation is not blocked, and the submitter (or, when unknown, at least one owner) has a verified e-mail address and two-factor authentication on. Evidence: the organisation status and each judged user's flags."
        ]
      },
      {
        heading: "The probe token",
        text:
          "An RS256 JWT signed by the hub, not by an installation: { iss: the hub's URL, sub: " +
          "\"app:<app id>\", aud: your client id (the live credential once you have one), " +
          "token_use: \"probe\", app_id, version_id, jti, iat, exp: iat + 300 }.\n\n" +
          "Its iss is not in the directory, so an app that verifies install tokens correctly " +
          "rejects it — and that passes: 400, 401 and 403 all count, because any HTTP answer below " +
          "500 proves the install URL is reachable. You do not need to recognise or validate it. " +
          "Just do not answer 5xx or hang on an issuer you do not know, and never provision " +
          "anything from it."
      },
      {
        heading: "frame-ancestors must cover every installation",
        text:
          "The check only proves the policy is restrictive; it cannot know which installations " +
          "your customers use. Serve frame-ancestors for the host that frames you (see \"Embedded " +
          "apps\"), and keep a restrictive default for requests without host, because that is " +
          "what the review and the nightly re-check fetch."
      },
      { see: ["embedding"] },
      {
        heading: "Nightly re-checks",
        text:
          "Every night at 03:20 UTC the live version of each live app re-runs https, " +
          "frame_ancestors and security_headers (not the probe, manifest, listing or standing). A " +
          "re-check never changes the app's status by itself.\n\n" +
          "A blocking failure (https or frame_ancestors) raises a probe_failures signal, a critical " +
          "\"Nightly check failed\" notice and an e-mail to the organisation's owners, admins and " +
          "support address. A security_headers failure alone is a warning notice. Failures on " +
          "three different nights within three days suspend the app automatically on every " +
          "installation. Fixing the problem is enough; there is nothing to resubmit."
      },
      {
        heading: "Blocks, suspensions and Notices",
        steps: [
          "Suspended — an automatic rule acted: probe_failures (above), token_replay_any (a refresh-token replay on any installation) or multi_install_abuse (the same local rule fired on two or more installations within 24 hours). The notice carries the rule, the evidence window and expires_at: an automatic suspension lifts after 24 hours unless Zarokaa confirms it.",
          "Blocked — Zarokaa blocked one app on every installation, or your whole organisation (every app). Two apps suspended or blocked within 30 days block the organisation automatically. Blocks never expire.",
          "Reinstated — a suspension was reverted by Zarokaa or expired unconfirmed.",
          "Unblocked — a block was lifted; the app returns to the status it had before.",
          "Review failed / warned and Nightly check failed / warning — with every open check and its evidence.",
          "Each of these is also e-mailed. A suspension that one installation applies on its own (its local rules) is not a hub notice; you see it only as EXT_INSTALL_INACTIVE from that installation."
        ]
      },
      {
        heading: "What a suspension or block does",
        text:
          "Within a minute on every installation: the app shows as suspended, every access and " +
          "refresh token of its installs is revoked, webhook subscriptions are disabled, the App " +
          "Store stops offering it, and the embedded app is replaced by Zarokaa's suspended panel " +
          "(the bridge emits suspended). Install data is kept.\n\n" +
          "When it is lifted, the app and its subscriptions come back automatically and the " +
          "embedded token exchange works again — but offline access does not: refresh tokens are " +
          "not reissued, so background jobs need a user to authorize again (see \"One app, many " +
          "Zarokaa installations\")."
      },
      { see: ["installations"] },
      {
        heading: "Asking for more later",
        text:
          "A new version that requests additional scopes does NOT widen existing " +
          "installations. They keep running on the set they were granted until each " +
          "customer re-consents, prompted by a banner in their App Store. Plan for both " +
          "sets to exist at once, and read the granted scopes from the token rather than " +
          "assuming your manifest."
      }
    ]
  }
];
